import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Wand2, Sparkles, Music, Upload, ImageIcon } from "lucide-react";
import { VoiceInput } from "@/components/VoiceInput";
import { ModelPicker } from "@/components/ModelPicker";

const GENRES = ["Gothic Sci-Fi", "Dark Fantasy", "Cyberpunk Fantasy", "High Fantasy", "Sci-Fi Mystery", "Sci-Fi Romance", "Cosmic Horror", "Steampunk Adventure"];
const TONES = ["atmospheric", "melancholy", "adventurous", "eerie", "hopeful", "cinematic"];

export default function Create() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [prompt, setPrompt] = useState(() => new URLSearchParams(window.location.search).get("prompt") || "");
  const contestId = new URLSearchParams(window.location.search).get("contest") || null;
  const [genre, setGenre] = useState("Dark Fantasy");
  const [tone, setTone] = useState("atmospheric");
  const [seriesId, setSeriesId] = useState("");
  const [mySeries, setMySeries] = useState([]);
  const [model, setModel] = useState("claude");
  const [busy, setBusy] = useState(false);

  // Per-chapter soundtracks picked at create time (file_id or "" for none).
  // Stories always generate 4 chapters.
  const [chapterTracks, setChapterTracks] = useState(["", "", "", ""]);
  const [chapterArts, setChapterArts] = useState(["", "", "", ""]);
  const [myTracks, setMyTracks] = useState([]);
  const [myArts, setMyArts] = useState([]);

  useEffect(() => {
    if (user) api.get("/series?mine=true").then(r => setMySeries(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get("/me/assets", { params: { kind: "user_music" } })
      .then(r => setMyTracks(r.data || []))
      .catch(() => {});
    api.get("/me/assets", { params: { kind: "user_art" } })
      .then(r => setMyArts(r.data || []))
      .catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <Sparkles className="h-10 w-10 text-amber-400 mx-auto mb-4" />
        <h1 className="font-serif text-3xl font-bold mb-3">Sign in to create for free</h1>
        <p className="text-slate-400 mb-6">Creation is unlimited and always free for logged-in writers.</p>
        <Link to="/login?next=/create" data-testid="create-signin" className="inline-flex items-center justify-center rounded-full bg-amber-500 text-black font-semibold px-7 h-12">Sign in to continue</Link>
      </div>
    );
  }

  const isSub = user.is_admin || (user.subscription_status && user.subscription_status !== "free");

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Give the story a spark first"); return; }
    setBusy(true);
    try {
      const body = { prompt, genre, tone, model, contest_id: contestId };
      if (seriesId) body.series_id = seriesId;
      const picked = chapterTracks.map((v) => v || null);
      if (picked.some(Boolean)) body.chapter_tracks = picked;
      const pickedArts = chapterArts.map((v) => v || null);
      if (pickedArts.some(Boolean)) body.chapter_arts = pickedArts;
      const { data } = await api.post("/stories/generate", body);
      toast.success(
        data.approval_status === "pending"
          ? `Submitted for review${data.charged_gems ? ` · ${data.charged_gems} gems` : ""} — +${data.pending_reward} gems when approved`
          : `Story published! +${data.credits_earned} gems`
      );
      await refresh();
      nav(`/story/${data.story.story_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="mb-10">
        <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-sky-400 mb-2">AI Story Studio · {isSub ? "Free with your subscription" : "20 gems · free for subscribers"}</div>
        <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight">Turn a spark into a story.</h1>
        <p className="mt-3 text-slate-400 max-w-2xl">Claude Sonnet 5 writes four chapters. Gemini Nano Banana paints the cover. You keep every word — and earn 50 gems once our editors approve it.</p>
        <Link to="/write" data-testid="create-write-own-link" className="mt-3 inline-block text-sm text-rose-300 hover:text-rose-200 underline underline-offset-4">Prefer to write it yourself? Submit a human-written story →</Link>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0F1117] p-6 md:p-10 space-y-6">
        <div>
          <div className="flex items-center justify-between mb-2 gap-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400">Your prompt</Label>
            <div className="flex items-center gap-2">
              <ModelPicker value={model} onChange={setModel} />
              <VoiceInput
                disabled={busy}
                onTranscript={(t) => setPrompt((prev) => (prev ? `${prev} ${t}` : t))}
              />
            </div>
          </div>
          <Textarea
            data-testid="create-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. A cartographer who maps the borders of dreams discovers one country is missing from every map she's ever drawn… (tap the mic to dictate)"
            className="min-h-[140px] bg-[#07080A] border-white/10 focus:border-amber-500/50 text-slate-100 text-base"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400">Genre</Label>
            <Select value={genre} onValueChange={setGenre}>
              <SelectTrigger data-testid="create-genre" className="mt-2 bg-[#07080A] border-white/10 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400">Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="create-tone" className="mt-2 bg-[#07080A] border-white/10 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {mySeries.length > 0 && (
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400">Add to a series (optional)</Label>
            <Select value={seriesId || "none"} onValueChange={(v) => setSeriesId(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="create-series" className="mt-2 bg-[#07080A] border-white/10 h-11">
                <SelectValue placeholder="Standalone story" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Standalone story</SelectItem>
                {mySeries.map((s) => <SelectItem key={s.series_id} value={s.series_id}>{s.title}{s.is_founding ? " · Founding" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="rounded-2xl border border-white/10 bg-[#07080A]/60 p-5 space-y-4" data-testid="create-soundtracks">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Music className="h-4 w-4 text-amber-400" />
                <Label className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400">Chapter soundtracks · optional</Label>
              </div>
              <p className="mt-1 text-xs text-slate-500">Pick one of your uploaded tracks for each of the four chapters. Readers hear it the moment the chapter opens.</p>
            </div>
            <Link
              to="/my/assets"
              data-testid="create-upload-music"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-[11px] text-slate-300 hover:border-amber-500/40 hover:text-amber-300"
            >
              <Upload className="h-3 w-3" /> Upload music
            </Link>
          </div>
          {myTracks.length === 0 ? (
            <div className="text-xs text-slate-500 italic">
              You haven't uploaded any music yet. Head to <Link to="/my/assets" className="text-amber-300 hover:underline">My Assets</Link> to upload a track, then come back — your tracks will show up here.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="text-[11px] text-slate-400 mb-1">Chapter {i + 1}</div>
                  <Select
                    value={chapterTracks[i] || "none"}
                    onValueChange={(v) => {
                      const next = [...chapterTracks];
                      next[i] = v === "none" ? "" : v;
                      setChapterTracks(next);
                    }}
                  >
                    <SelectTrigger data-testid={`create-track-${i}`} className="bg-[#0F1117] border-white/10 h-10 text-sm">
                      <SelectValue placeholder="No track" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No track</SelectItem>
                      {myTracks.map((t) => (
                        <SelectItem key={t.file_id} value={t.file_id}>
                          {t.title || t.filename || t.file_id.slice(0, 10)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {chapterTracks[i] && (() => {
                    const t = myTracks.find((x) => x.file_id === chapterTracks[i]);
                    return t ? (
                      <audio
                        data-testid={`create-track-preview-${i}`}
                        controls
                        preload="none"
                        src={fileUrl(t.url)}
                        className="mt-2 w-full h-8"
                      />
                    ) : null;
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#07080A]/60 p-5 space-y-4" data-testid="create-chapter-arts">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-sky-400" />
                <Label className="font-mono text-[10px] uppercase tracking-[0.24em] text-sky-400">Chapter paintings · optional</Label>
              </div>
              <p className="mt-1 text-xs text-slate-500">Pin one of your uploaded paintings above each chapter. Readers see it before the prose begins.</p>
            </div>
            <Link
              to="/my/assets"
              data-testid="create-upload-art"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-[11px] text-slate-300 hover:border-sky-500/40 hover:text-sky-300"
            >
              <Upload className="h-3 w-3" /> Upload painting
            </Link>
          </div>
          {myArts.length === 0 ? (
            <div className="text-xs text-slate-500 italic">
              You haven't uploaded any paintings yet. Head to <Link to="/my/assets" className="text-sky-300 hover:underline">My Assets</Link> to upload artwork, then come back.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="text-[11px] text-slate-400 mb-1">Chapter {i + 1}</div>
                  <Select
                    value={chapterArts[i] || "none"}
                    onValueChange={(v) => {
                      const next = [...chapterArts];
                      next[i] = v === "none" ? "" : v;
                      setChapterArts(next);
                    }}
                  >
                    <SelectTrigger data-testid={`create-art-${i}`} className="bg-[#0F1117] border-white/10 h-10 text-sm">
                      <SelectValue placeholder="No painting" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No painting</SelectItem>
                      {myArts.map((a) => (
                        <SelectItem key={a.file_id} value={a.file_id}>
                          {a.title || a.filename || a.file_id.slice(0, 10)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {chapterArts[i] && (() => {
                    const a = myArts.find((x) => x.file_id === chapterArts[i]);
                    return a ? (
                      <img
                        data-testid={`create-art-preview-${i}`}
                        src={fileUrl(a.url)}
                        alt={a.title || "painting"}
                        className="mt-2 w-full h-24 object-cover rounded-lg border border-white/10"
                      />
                    ) : null;
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
        <Button
          data-testid="create-generate"
          onClick={generate}
          disabled={busy}
          className="w-full h-14 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold text-base"
        >
          {busy ? (
            <>
              <div className="h-5 w-5 rounded-full border-2 border-black/30 border-t-black animate-spin mr-2" />
              Weaving your story… (about 30s)
            </>
          ) : (
            <><Wand2 className="h-5 w-5 mr-2" /> Generate &amp; submit — {isSub ? "free" : "20 gems"}</>
          )}
        </Button>
        <p data-testid="create-review-note" className="text-center text-xs text-slate-500">Every story is reviewed before it goes public · 50 gems on approval (100 for contest entries) · gems refunded if declined</p>
      </div>
    </div>
  );
}
